import { AD_SLOTS } from '@zillow/ad-state-machine';

import {
    AD_NETWORK,
    AD_MESSAGE_DATA,
    NAV_AD_TYPE,
    DISPLAY_AD_TYPE,
    AD_MESSAGE_ORIGIN,
    NAV_AD_RENDER_EVENT,
    DISPLAY_AD_RENDER_EVENT,
    VELOCITY_CONTROL_VERS,
} from '../constants';

import { trackNavAdDisplayState, trackDisplayAdDisplayState } from '../util/tracking/tracking';

export function deviceType(type = {}) {
    const { isMobile, isTablet } = type;
    let device = 'desktop';
    if (isMobile && isTablet) {
        device = 'tablet';
        return device;
    }
    if (isMobile) {
        device = 'mobile';
        return device;
    }
    if (isTablet) {
        device = 'tablet';
        return device;
    }
    return device;
}

let adRendered = false;
export const isNavAdSlot = adType => adType === NAV_AD_TYPE;

export const isDisplayAdSlot = adType => adType === DISPLAY_AD_TYPE;

export const onSlotOnload = e => {
    const eventSlot = e.slot.getSlotId().toString();
    const iFrameSelector = `google_ads_iframe_${eventSlot}`;
    const iFrameNode = document.getElementById(iFrameSelector);
    if (iFrameNode && !iFrameNode.getAttribute('loading')) {
        iFrameNode.setAttribute('loading', 'auto');
    }
};

export const getSlotSize = (adType, iabTestEnabled) => {
    switch (adType) {
        case DISPLAY_AD_TYPE:
            return [300, 250];
        case NAV_AD_TYPE:
        default:
            return iabTestEnabled ? [[300, 250], ['fluid']] : 'fluid';
    }
};

export const isAdRestricted = slot => {
    const isRestricted = slot.includes('_restricted');
    return isRestricted;
};

export const getSlotNameFromPath = adUnitPath => {
    if (!adUnitPath || typeof adUnitPath !== 'string') {
        return '';
    }
    const splitPath = adUnitPath.split('/');
    return splitPath[splitPath.length - 1];
};

/**
 * Fired when GPT renders any slot
 */
export const onSlotRenderEnded = (e, adType, onSlotRenderEndedSlot, slot) => {
    const eventSlotName = getSlotNameFromPath(e.slot.getAdUnitPath());
    const slotName = getSlotNameFromPath(slot);
    let adRenderEvent = '';
    let adStateTracker = null;

    if (isNavAdSlot(adType)) {
        adRenderEvent = NAV_AD_RENDER_EVENT;
        adStateTracker = trackNavAdDisplayState;
    } else if (isDisplayAdSlot(adType)) {
        adRenderEvent = DISPLAY_AD_RENDER_EVENT;
        adStateTracker = trackDisplayAdDisplayState;
    }

    if (!adRendered && adRenderEvent.length > 0) {
        adRendered = true;
    }

    if (adStateTracker !== null) {
        adStateTracker(e);
    }

    if (onSlotRenderEndedSlot && slotName === eventSlotName) {
        onSlotRenderEndedSlot(e);
    }
};

export const onMessage = (e, adType, onSlotRenderEndedSlot) => {
    if (adType === DISPLAY_AD_TYPE) {
        return;
    }
    if (AD_MESSAGE_ORIGIN.test(e.origin) && e.data === AD_MESSAGE_DATA) {
        onSlotRenderEndedSlot({
            isEmpty: true,
        });
    }
};

export const updateVersValue = (targets, versOn = '') => {
    const currentVers = targets?.vers;
    const mstVersValue = versOn !== '' ? `${currentVers},${versOn}` : `${currentVers}`;
    const mergedTargets = { ...targets, vers: mstVersValue };
    return mergedTargets;
};

export const shouldAdHavePrebid = ({ slot, adPrebidScriptFailed, isPrebidTrialOn }) => {
    if (!isPrebidTrialOn) {
        return false;
    }
    const isRestricted = isAdRestricted(`/${AD_NETWORK}/${slot}`);
    return !isRestricted && !adPrebidScriptFailed;
};

export const shouldAdHavePrebidState = ({
    isRestricted,
    adPrebidScriptFailed,
    isPrebidEnabled,
}) => {
    if (!isPrebidEnabled) {
        return false;
    }
    return !isRestricted && !adPrebidScriptFailed;
};

export const isIABSupported = ({ numListColumns, isTall, isMobile }) => {
    const isColumnEligible = numListColumns > 0;
    const deviceSupported = isMobile && isTall;
    if (deviceSupported) {
        return true;
    }
    return isColumnEligible;
};

export const getAdAbTrialMapping = () => ({
    MST_SRP_WEB_SPEED: {
        slots: [AD_SLOTS.NAV, AD_SLOTS.NAV2, AD_SLOTS.RENTAL, AD_SLOTS.DISPLAY],
        vers: {
            CONTROL: VELOCITY_CONTROL_VERS,
            ON: 'z_srp_web_velocity-test',
            NEXT_HEAD: 'z_srp_web_velocity-test_a',
            NEXT_SCRIPT: 'z_srp_web_velocity-test_b',
        },
    },
    MST_SRP_WEB_2NDAD13AND17: {
        slots: [AD_SLOTS.NAV, AD_SLOTS.NAV2, AD_SLOTS.RENTAL, AD_SLOTS.DISPLAY],
        vers: {
            CONTROL: 'z_srp_web_2ndad-13and17_control',
            SLOT13: 'z_srp_web_2ndad-13and17_test_a',
            SLOT17: 'z_srp_web_2ndad-13and17_test_b',
        },
    },
});

export const getAdAbTrialVers = (trialName, treatmentName, defaultValue) => {
    const trialMapping = getAdAbTrialMapping();
    if (!trialMapping[trialName]?.vers?.[treatmentName]) {
        return defaultValue;
    }
    return trialMapping[trialName].vers[treatmentName];
};

export const getAdTargetDiv = adSlot => {
    switch (adSlot) {
        case AD_SLOTS.NAV:
            return 'srp_main_slot_box_1';
        case AD_SLOTS.RENTAL:
            return 'srp_rent_slot_box_1';
        case AD_SLOTS.NAV2:
            return 'srp_main_slot_box_2';
        default:
            return 'srp_iab_slot_box_1';
    }
};

export const getAdSlotLazyLoadDistance = adSlot => {
    switch (adSlot) {
        case AD_SLOTS.NAV2:
            return '-530px';
        default:
            return null;
    }
};

export const getSrpPageVariant = srpState => {
    const isNFS = !!srpState?.queryState?.filterState?.isRecentlySold?.value;
    const isForRent = !!srpState?.queryState?.filterState?.isForRent?.value;

    if (isNFS) {
        return 'nfs';
    }
    if (isForRent) {
        return 'fr';
    }
    return 'fs';
};
